import React from 'react';

import { format } from 'date-fns';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';

import '../ThemeDistribution/styles.css';
import { OneOnOneInsightsCompletionGraphData } from './service';

interface Props {
  data: OneOnOneInsightsCompletionGraphData;
  chartRef?: React.MutableRefObject<HTMLDivElement>;
}

const generateMonthsBetweenDateRange = (startDate: Date, endDate: Date) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const months = [];
  while (start <= end) {
    months.push(format(new Date(start), 'yyyy-MM-dd'));
    start.setMonth(start.getMonth() + 1);
  }
  return months;
};

const optionsGenerator = ({
  series,
  xAxisCategories,
}: {
  series: Highcharts.SeriesOptionsType[];
  xAxisCategories?: Array<string>;
}): Highcharts.Options => ({
  chart: {
    type: 'line',
  },
  title: {
    text: '',
  },
  xAxis: {
    categories: xAxisCategories,
    labels: {
      formatter: ({ value }) => format(new Date(value), 'MMM, yy'),
    },
  },
  yAxis: {
    title: {
      text: 'Value',
    },
    max: 100, // Limit the y-axis to a maximum value of 100
  },
  exporting: {
    enabled: false, // Set the exporting (hamburger) menu to false to remove it
  },
  legend: {
    layout: 'horizontal',
    symbolRadius: 2,
  },
  plotOptions: {
    series: {
      label: {
        connectorAllowed: false,
      },
    },
  },
  credits: {
    enabled: false,
  },
  tooltip: {
    // headerFormat: '<b>{point.x}</b><br/>',
    // pointFormat: '<b>{series.name}: {point.y}</b> <br/>Total: {point.stackTotal}',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderColor: '#F2F2F2',
    padding: 12,
    shape: 'square',
    style: {
      minHeight: '104px',
      boxShadow: '0 3px 14px rgb(0 0 0 / 40%)',
    },
    formatter() {
      const chart = this;
      const { x, y, series, color } = chart ?? {};
      const isCompletion = series?.name === 'Completion %';
      const formattedY = Number.isFinite(y) && Math.round(y); // Limit y to two decimal places
      const header = `<div class="highcharts-tooltip-flex"> 
      <div class="highcharts-tooltip-edge" style="background-color: ${color}; height: 43px;"></div>
      <div class="highcharts-tooltip-flex-column">
      <b>${series.name}: ${formattedY}${
        isCompletion ? '%' : ''
      } </b><br/><p style="color:#666666; margin-bottom: 0px;">${format(new Date(x), 'MMM, yy')}</p>
      </div>
      </div
      `;
      return header;
    },
    useHTML: true,
  },
  series,
});

const CompletionLineChart = ({ data, chartRef }: Props) => {
  const { ratingData, completionData } = data ?? {};
  const series = [];

  const { dateRange } = useOneOnOneInsightsFiltersData();
  const { startDate, endDate } = dateRange;

  // Ensure startDate and endDate starts from the first day of its month
  const startDateFirstDay = new Date(startDate);
  startDateFirstDay.setDate(1);
  const endDateFirstDay = new Date(endDate);
  endDateFirstDay.setDate(1);

  // generating selected date values on UI
  const xAxisCategories = generateMonthsBetweenDateRange(startDateFirstDay, endDateFirstDay);

  const ratingValue = xAxisCategories?.map((date) => {
    const value = ratingData?.[date] ?? null;
    return value !== null && !isNaN(value) ? Math.round(value) : null;
  });

  const completionValue = xAxisCategories?.map((date) => completionData?.[date] ?? null);

  ratingData &&
    series.push({
      name: 'Satisfaction Score',
      data: ratingValue,
      color: '#ACBAFF',
      type: 'column',
      dataLabels: {
        enabled: true,
        rotation: 0,
        color: '#333333',
        align: 'center',
        y: 30, // 10 pixels down from the top,
        borderColor: 'none',
        borerWidth: 0,
        stroke: 'none',
        style: {
          textOutline: 'transparent',
        },
      },
    });

  completionData &&
    series.push({
      name: 'Completion %',
      data: completionValue,
      color: '#44812F',
      type: 'line',
    });

  return (
    <div ref={chartRef} style={{ height: '100%' }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={optionsGenerator({
          //   @ts-ignore
          series,
          xAxisCategories,
        })}
        ref={chartRef}
        containerProps={{ style: { height: '100%' } }}
      />
    </div>
  );
};

export { CompletionLineChart };
